import React, { useState, useEffect } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import Header from "../../../Header";
import Footer from "../../../Footer";
import { checkIsAdminLoggined } from "../../../../utils/utils.js";
import { recalculateBasket, IMPERSONATION_ADMIN_TOKEN_KEY } from "../../../../shared/constants";
import axios from "axios";
import { Helmet } from "react-helmet";
import { Col, Row, Table, Form, Button, Badge, Card, Modal } from "react-bootstrap";
import "../../../../styles/admin-users.css";

const AdminUserDetailPage = props => {
  const { userId } = useParams();
  const history = useHistory();
  const token = localStorage.getItem("token");
  const [userDetail, setUserDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditDiscountModal, setShowEditDiscountModal] = useState(false);
  const [newManualDiscountLevel, setNewManualDiscountLevel] = useState("");
  const [showEditCommentModal, setShowEditCommentModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [newAdminComment, setNewAdminComment] = useState("");
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateError, setImpersonateError] = useState(null);
  const [discountLevelsList, setDiscountLevelsList] = useState([]);
  const [sendSmsWhenSettingManualDiscount, setSendSmsWhenSettingManualDiscount] = useState(false);
  const [showEditProfileNameModal, setShowEditProfileNameModal] = useState(false);
  const [editProfileName, setEditProfileName] = useState("");
  const [editProfileSurname, setEditProfileSurname] = useState("");
  const [editProfileNameSaving, setEditProfileNameSaving] = useState(false);
  const [editProfileNameError, setEditProfileNameError] = useState("");

  useEffect(() => {
    checkIsAdminLoggined(props);
    window.scrollTo(0, 0);
    loadUserDetail();
  }, [userId]);

  useEffect(() => {
    if (showEditDiscountModal && discountLevelsList.length === 0) {
      axios
        .get('/api/v1/user/discount-level', { headers: { Authorization: token } })
        .then(res => {
          if (res.data && res.data.data) setDiscountLevelsList(res.data.data);
        })
        .catch(() => {});
    }
  }, [showEditDiscountModal]);

  const loadUserDetail = () => {
    setIsLoading(true);
    axios
      .get(`/api/v1/admin/users/${userId}`, {
        headers: {
          Authorization: token,
        },
      })
      .then(res => {
        if (res.status === 200 && res.data.data) {
          setUserDetail(res.data.data);
        }
        setIsLoading(false);
      })
      .catch(error => {
        console.error("Error loading user detail:", error);
        setIsLoading(false);
      });
  };

  const handleUpdateManualDiscountLevel = () => {
    const level = newManualDiscountLevel === "" ? null : parseInt(newManualDiscountLevel, 10);
    const payload = level != null
      ? { manualDiscountLevel: level, sendSmsWhenSettingManualDiscountLevel: sendSmsWhenSettingManualDiscount || undefined }
      : { clearManualDiscountLevel: true };
    axios
      .put(
        `/api/v1/admin/users/${userId}`,
        payload,
        {
          headers: {
            Authorization: token,
          },
        }
      )
      .then(res => {
        if (res.status === 200) {
          setShowEditDiscountModal(false);
          setSendSmsWhenSettingManualDiscount(false);
          loadUserDetail();
        }
      })
      .catch(error => {
        console.error("Error updating manual discount level:", error);
      });
  };

  const selectedLevelInfo = newManualDiscountLevel
    ? discountLevelsList.find((l) => String(l.level) === String(newManualDiscountLevel))
    : null;
  const isSelectedLevelHidden = Boolean(selectedLevelInfo && selectedLevelInfo.hidden);

  const handleUpdateAdminComment = () => {
    axios
      .put(
        `/api/v1/admin/users/${userId}/order/${selectedOrderId}/admin-comment`,
        { adminComment: newAdminComment },
        {
          headers: {
            Authorization: token,
          },
        }
      )
      .then(res => {
        if (res.status === 200) {
          setShowEditCommentModal(false);
          setSelectedOrderId(null);
          setNewAdminComment("");
          loadUserDetail();
        }
      })
      .catch(error => {
        console.error("Error updating admin comment:", error);
      });
  };

  const handleSaveProfileName = () => {
    setEditProfileNameSaving(true);
    setEditProfileNameError("");
    axios
      .put(
        `/api/v1/admin/users/${userId}`,
        { name: editProfileName.trim() || null, surname: editProfileSurname.trim() || null },
        { headers: { Authorization: token } }
      )
      .then(() => {
        setShowEditProfileNameModal(false);
        loadUserDetail();
      })
      .catch(err => {
        setEditProfileNameError(err.response?.data?.errorMessage || "Помилка збереження");
      })
      .finally(() => setEditProfileNameSaving(false));
  };

  const handleClearManualDiscount = () => {
    axios
      .put(
        `/api/v1/admin/users/${userId}`,
        { clearManualDiscountLevel: true },
        { headers: { Authorization: token } }
      )
      .then(() => {
        loadUserDetail();
      })
      .catch((error) => console.error("Error clearing manual discount:", error));
  };

  const openEditCommentModal = (order) => {
    setSelectedOrderId(order.orderId);
    setNewAdminComment(order.adminComment || "");
    setShowEditCommentModal(true);
  };

  const handleImpersonate = () => {
    setImpersonateError(null);
    setImpersonating(true);
    const adminToken = localStorage.getItem("token");
    sessionStorage.setItem(IMPERSONATION_ADMIN_TOKEN_KEY, adminToken);
    axios
      .post(`/api/v1/admin/users/${userId}/impersonate`, {}, {
        headers: { Authorization: adminToken },
      })
      .then((res) => {
        if (res.data && res.data.data && res.data.data.token) {
          localStorage.setItem("token", res.data.data.token);
          recalculateBasket(res.data.data.token);
          history.push("/user/personal-cabinet");
        }
      })
      .catch((err) => {
        setImpersonating(false);
        sessionStorage.removeItem(IMPERSONATION_ADMIN_TOKEN_KEY);
        const msg = err.response?.data?.errorMessage || err.response?.data?.data || "Помилка входу";
        setImpersonateError(typeof msg === "string" ? msg : "Не вдалося увійти як користувач");
      });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0 грн";
    return `${amount.toLocaleString("uk-UA")} грн`;
  };

  if (isLoading) {
    return (
      <div className="container">
        <div className="row">
          <div className="col p-0">
            <Header />
          </div>
        </div>
        <div className="text-center p-4">
          <p>Завантаження...</p>
        </div>
        <div className="row">
          <div className="col p-0">
            <Footer />
          </div>
        </div>
      </div>
    );
  }

  if (!userDetail) {
    return (
      <div className="container admin-user-detail-page">
        <div className="row">
          <div className="col p-0">
            <Header />
          </div>
        </div>
        <div className="text-center p-4">
          <p>Користувача не знайдено</p>
          <Link to="/admin/users" className="btn btn-dark">
            Повернутися до списку
          </Link>
        </div>
        <div className="row">
          <div className="col p-0">
            <Footer />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container admin-user-detail-page">
      <div className="row">
        <div className="col p-0 pb-3">
          <Header />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col p-0">
          <Button variant="secondary" onClick={() => history.push("/admin/users")}>
            ← Повернутися до списку
          </Button>
        </div>
      </div>

      <div className="row">
        <div className="col p-0">
          <h2>Користувач #{userDetail.id}</h2>
        </div>
      </div>

      {/* Core User Information */}
      <div className="row mb-3">
        <div className="col p-0">
          <Card>
            <Card.Header>
              <h5>Основна інформація</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><strong>ID:</strong> {userDetail.id}</p>
                  <p className="d-flex align-items-center">
                    <strong>Ім'я (профіль):</strong> <span className="ml-1">{userDetail.name || <span className="text-muted">-</span>}</span>
                    <button type="button" className="btn btn-link btn-sm p-0 ml-1 text-dark" onClick={() => { setEditProfileName(userDetail.name || ""); setEditProfileSurname(userDetail.surname || ""); setEditProfileNameError(""); setShowEditProfileNameModal(true); }} title="Редагувати ім'я та прізвище" aria-label="Редагувати ім'я та прізвище">
                      <i className="bi bi-pencil-fill text-dark" style={{ fontSize: "0.9rem" }} />
                    </button>
                  </p>
                  <p className="d-flex align-items-center">
                    <strong>Прізвище (профіль):</strong> <span className="ml-1">{userDetail.surname || <span className="text-muted">-</span>}</span>
                    <button type="button" className="btn btn-link btn-sm p-0 ml-1 text-dark" onClick={() => { setEditProfileName(userDetail.name || ""); setEditProfileSurname(userDetail.surname || ""); setEditProfileNameError(""); setShowEditProfileNameModal(true); }} title="Редагувати ім'я та прізвище" aria-label="Редагувати ім'я та прізвище">
                      <i className="bi bi-pencil-fill text-dark" style={{ fontSize: "0.9rem" }} />
                    </button>
                  </p>
                  <p><strong>Логін:</strong> {userDetail.login || <span className="text-muted">-</span>}</p>
                  <p><strong>Email:</strong> {userDetail.email || <span className="text-muted">-</span>}</p>
                  <p><strong>Телефон:</strong> {userDetail.phone || <span className="text-muted">-</span>}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Тип:</strong> <Badge variant={userDetail.type === "ADMIN" ? "danger" : userDetail.type === "SUPER_USER" ? "warning" : "secondary"}>{userDetail.type || "USER"}</Badge></p>
                  <p><strong>Рівень знижки (автоматичне обчислений):</strong> {userDetail.discountLevel ?? "0"}{userDetail.discountLevelName ? ` - ${userDetail.discountLevelName}` : ""}</p>
                  <p>
                    <strong>Ручний рівень знижки:</strong>{" "}
                    {userDetail.manualDiscountLevel != null ? userDetail.manualDiscountLevel : "—"}
                    {userDetail.manualDiscountLevel != null && (
                      <Button variant="link" size="sm" onClick={handleClearManualDiscount} className="text-dark ml-1">Очистити</Button>
                    )}
                    <Button variant="link" size="sm" className="text-dark" onClick={() => {
                      setNewManualDiscountLevel(userDetail.manualDiscountLevel != null ? String(userDetail.manualDiscountLevel) : "");
                      setShowEditDiscountModal(true);
                    }}>
                      {userDetail.manualDiscountLevel != null ? "Змінити" : "Встановити"}
                    </Button>
                  </p>
                  <p>
                    <strong>Фактичний рівень знижки:</strong>{" "}
                    {Math.max(userDetail.discountLevel ?? 0, userDetail.manualDiscountLevel ?? 0)}
                  </p>
                  <p><strong>Дата реєстрації:</strong> {userDetail.createTime || <span className="text-muted">-</span>}</p>
                </Col>
              </Row>
              {userDetail.type !== "ADMIN" && (
                <Row className="mt-2">
                  <Col>
                    <Button
                      variant="outline-dark"
                      onClick={handleImpersonate}
                      disabled={impersonating}
                    >
                      {impersonating ? "Вхід…" : "Увійти як цей користувач"}
                    </Button>
                    {impersonateError && (
                      <div className="text-danger mt-2 small">{impersonateError}</div>
                    )}
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Contact & Delivery Info */}
      {userDetail.userInfo && (
        <div className="row mb-3">
          <div className="col p-0">
            <Card>
              <Card.Header>
                <h5>Контактна інформація та доставка</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>Ім'я:</strong> {userDetail.userInfo.name || <span className="text-muted">-</span>}</p>
                    <p><strong>Прізвище:</strong> {userDetail.userInfo.surname || <span className="text-muted">-</span>}</p>
                    <p><strong>Телефон:</strong> {userDetail.userInfo.phone || <span className="text-muted">-</span>}</p>
                    <p><strong>Місто:</strong> {userDetail.userInfo.city || <span className="text-muted">-</span>}</p>
                  </Col>
                  <Col md={6}>
                    {userDetail.userInfo.receiveMethod && (
                      <p><strong>Спосіб отримання:</strong> {userDetail.userInfo.receiveMethod.description || <span className="text-muted">-</span>}</p>
                    )}
                    {userDetail.userInfo.departmentNumber && (
                      <p><strong>Відділення:</strong> {userDetail.userInfo.departmentNumber}</p>
                    )}
                    {userDetail.userInfo.street && (
                      <p><strong>Вулиця:</strong> {userDetail.userInfo.street} {userDetail.userInfo.buildingNumber} {userDetail.userInfo.flatNumber}</p>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        </div>
      )}

      {/* Orders Summary */}
      <div className="row mb-3">
        <div className="col p-0">
          <Card>
            <Card.Header>
              <h5>Замовлення - Підсумок</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <p><strong>Всього замовлень:</strong> {userDetail.totalOrders || 0}</p>
                </Col>
                <Col md={4}>
                  <p><strong>Витрачена сума:</strong> {formatCurrency(userDetail.totalSpent || 0)}</p>
                </Col>
                <Col md={4}>
                  <p><strong>Середнє значення замовлення:</strong> {formatCurrency(userDetail.averageOrderValue || 0)}</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Orders List */}
      <div className="row mb-3">
        <div className="col p-0">
          <Card>
            <Card.Header>
              <h5>Список замовлень</h5>
            </Card.Header>
            <Card.Body>
              {userDetail.orders && userDetail.orders.length > 0 ? (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Дата</th>
                      <th>Статус</th>
                      <th>Оплачено</th>
                      <th>Спосіб оплати</th>
                      <th>Ціна</th>
                      <th>Ціна зі знижкою</th>
                      <th>Коментар користувача</th>
                      <th>Коментар адміна</th>
                      <th>Дії</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userDetail.orders.map(order => (
                      <tr key={order.orderId}>
                        <td>
                          <Link to={`/admin/order/${order.orderId}`} className="text-dark">
                            #{order.orderId}
                          </Link>
                        </td>
                        <td>{order.createTime || <span className="text-muted">-</span>}</td>
                        <td>{order.status?.adminValue || order.status?.databaseValue || <span className="text-muted">-</span>}</td>
                        <td>{order.paid?.adminValue || order.paid?.databaseValue || <span className="text-muted">-</span>}</td>
                        <td>{order.payment?.adminValue || order.payment?.databaseValue || <span className="text-muted">-</span>}</td>
                        <td>{formatCurrency(order.price || 0)}</td>
                        <td>{formatCurrency(order.discountedPrice || 0)}</td>
                        <td>{order.userComment || <span className="text-muted">-</span>}</td>
                        <td>
                          {order.adminComment || <span className="text-muted">-</span>}
                          <Button
                            variant="link"
                            size="sm"
                            className="text-dark"
                            onClick={() => openEditCommentModal(order)}
                          >
                            Редагувати
                          </Button>
                        </td>
                        <td>
<Link to={`/admin/order/${order.orderId}`} className="btn btn-sm btn-dark">
                                            Деталі
                                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">Замовлень немає</p>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Favorite Products */}
      <div className="row mb-3">
        <div className="col p-0">
          <Card>
            <Card.Header>
              <h5>Обрані товари</h5>
            </Card.Header>
            <Card.Body>
              {userDetail.favoriteProducts && userDetail.favoriteProducts.length > 0 ? (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Назва</th>
                      <th>Статус</th>
                      <th>Додано до обраних</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userDetail.favoriteProducts.map(favorite => (
                      <tr key={favorite.productId}>
                        <td>{favorite.productId}</td>
                        <td>{favorite.productName || <span className="text-muted">-</span>}</td>
                        <td>{favorite.productState?.clientValue || favorite.productState?.databaseValue || <span className="text-muted">-</span>}</td>
                        <td>{favorite.addedToFavoritesDate || <span className="text-muted">-</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">Обрані товари відсутні</p>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Edit Manual Discount Level Modal */}
      <Modal show={showEditDiscountModal} onHide={() => setShowEditDiscountModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Встановити ручний рівень знижки</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Рівень знижки (вручну)</Form.Label>
            <Form.Control
              as="select"
              value={newManualDiscountLevel}
              onChange={(e) => {
                setNewManualDiscountLevel(e.target.value);
                setSendSmsWhenSettingManualDiscount(false);
              }}
            >
              <option value="">Оберіть рівень</option>
              {discountLevelsList.map((l) => (
                <option key={l.level} value={l.level}>
                  {l.level} - {l.levelName || `Рівень ${l.level}`}{l.hidden ? ' (прихований)' : ''}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
          {isSelectedLevelHidden && (
            <Form.Group className="mt-2">
              <Form.Check
                type="checkbox"
                id="sendSmsManualDiscount"
                label="Надіслати SMS користувачу про встановлення персональних умов знижки"
                checked={sendSmsWhenSettingManualDiscount}
                onChange={(e) => setSendSmsWhenSettingManualDiscount(e.target.checked)}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditDiscountModal(false)}>
            Скасувати
          </Button>
          <Button variant="dark" onClick={handleUpdateManualDiscountLevel}>
            Зберегти
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Admin Comment Modal */}
      <Modal show={showEditCommentModal} onHide={() => setShowEditCommentModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Редагувати коментар адміна</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Коментар адміна</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newAdminComment}
              onChange={(e) => setNewAdminComment(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditCommentModal(false)}>
            Скасувати
          </Button>
          <Button variant="dark" onClick={handleUpdateAdminComment}>
            Зберегти
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit profile name/surname — Bootstrap classes only */}
      {showEditProfileNameModal && (
        <>
          <div className="modal-backdrop show" style={{ zIndex: 1040 }} onClick={() => !editProfileNameSaving && setShowEditProfileNameModal(false)} />
          <div
            className="modal show"
            tabIndex={-1}
            role="dialog"
            style={{ display: "block", zIndex: 1041 }}
            onClick={() => !editProfileNameSaving && setShowEditProfileNameModal(false)}
          >
            <div className="modal-dialog modal-dialog-centered" role="document" onClick={e => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Редагувати ім'я та прізвище (профіль)</h5>
                  <button type="button" className="close" aria-label="Close" onClick={() => !editProfileNameSaving && setShowEditProfileNameModal(false)}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Ім'я:</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Введіть ім'я"
                      value={editProfileName}
                      onChange={e => setEditProfileName(e.target.value)}
                      disabled={editProfileNameSaving}
                    />
                  </div>
                  <div className="form-group">
                    <label>Прізвище:</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Введіть прізвище"
                      value={editProfileSurname}
                      onChange={e => setEditProfileSurname(e.target.value)}
                      disabled={editProfileNameSaving}
                    />
                  </div>
                  {editProfileNameError && (
                    <div className="alert alert-danger mt-2 mb-0" role="alert">
                      {editProfileNameError}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditProfileNameModal(false)} disabled={editProfileNameSaving}>
                    Скасувати
                  </button>
                  <button type="button" className="btn btn-dark" onClick={handleSaveProfileName} disabled={editProfileNameSaving}>
                    {editProfileNameSaving ? "Збереження…" : "Зберегти"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="row">
        <div className="col p-0">
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetailPage;
